import { useState, useEffect } from 'react';
import { Route, useLocation, Routes } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import ReactGA from 'react-ga4';
import { HelmetProvider } from 'react-helmet-async';

import NavigationBar from './02_COMPONENTS/01_navbar/navbar.jsx';
import Footer from './02_COMPONENTS/02_footer/Footer.jsx';
import Loader from './02_COMPONENTS/09_loader/loader.jsx';
import CookieConsent from './02_COMPONENTS/11_cookie-consent/CookieConsent.jsx';
import ScrollToTop from './components/ScrollToTop.jsx';
import { ThemeProvider } from './contexts/ThemeContext.jsx';
import { CookieProvider } from './contexts/CookieContext.jsx';

import './App.scss';

const Initial = lazy(() => import('./03_NAVIGATION/00_initial/initial.jsx'));
const Home = lazy(() => import('./03_NAVIGATION/01_home/home.jsx'));
const Hunting = lazy(() => import('./03_NAVIGATION/02_hunting/hunting.jsx'));
const Fishing = lazy(() => import('./03_NAVIGATION/03_fishing/fishing.jsx'));
const Nature = lazy(() => import('./03_NAVIGATION/04_nature/nature.jsx'));
const About = lazy(() => import('./03_NAVIGATION/05_about/about.jsx'));
const Contact = lazy(() => import('./03_NAVIGATION/06_contact/contact.jsx'));
const ThankYou = lazy(() => import('./03_NAVIGATION/07_thank-you/thank-you.jsx'));
const EmailPreview = lazy(() => import('./03_NAVIGATION/08_email-preview/EmailPreview.jsx'));
const FAQ = lazy(() => import('./03_NAVIGATION/09_faq/faq.jsx'));

function App() {
  const { i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const language = localStorage.getItem('language');

    if (language) {
      i18n.changeLanguage(language);
      setLoading(false);
    } else {
      setLoading(true);
    }
  }, [i18n]);

  // Track page views with GA4
  useEffect(() => {
    ReactGA.send({
      hitType: 'pageview',
      page: location.pathname + location.search,
      title: document.title
    });
  }, [location]);

  // Fix viewport height for mobile browsers
  useEffect(() => {
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    // Set on mount
    setVH();

    // Update on resize and orientation change
    window.addEventListener('resize', setVH);
    window.addEventListener('orientationchange', setVH);

    return () => {
      window.removeEventListener('resize', setVH);
      window.removeEventListener('orientationchange', setVH);
    };
  }, []);

  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

  return (
    <HelmetProvider>
      <CookieProvider>
        {loading ? (
          <Initial setLoading={setLoading} />
        ) : (
        <ThemeProvider>
          <ScrollToTop />
          <div className="App">
            <div className='lapponia-silent'>
              <header>
                <NavigationBar />
              </header>
              <main>
                <Suspense fallback={<Loader />}>
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/hunting" element={<Hunting />} />
                    <Route path="/fishing" element={<Fishing />} />
                    <Route path="/nature" element={<Nature />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/faq" element={<FAQ />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/thank-you" element={<ThankYou />} />
                    {isLocalhost && <Route path="/email-preview" element={<EmailPreview />} />}
                  </Routes>
                </Suspense>
              </main>
              <Footer />
              <CookieConsent />
            </div>
          </div>
        </ThemeProvider>
        )}
      </CookieProvider>
    </HelmetProvider>
  );
}

export default App;
